import React, { useState, useEffect } from "react";
import Switch from "react-switch";
import MenuProcessService from "../services/MenuProcessService";
import EmployeeProcessService from "../services/EmployeeProcessService";

function DepartmentRights() {
    const usercode = localStorage.getItem('empcode');
    const [empCode, setEmpCode] = useState("");
    const [selectedPlant, setSelectedPlant] = useState("");
    const [plants, setPlants] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    // ✅ Load Plants
    useEffect(() => {
        loadPlants();
    }, []);

    const loadPlants = async () => {
        try {
            const res = await EmployeeProcessService.getPlant(usercode);
            if (res?.data?.statusCode === 200) {
                setPlants(res.data.result);
            }
        } catch (error) {
            console.error("Error loading plants:", error);
        }
    };

    // ✅ Handle Plant Change
    const handlePlantChange = (e) => {
        setSelectedPlant(e.target.value);
        setDepartments([]);
        setSelectAll(false);
    };

    // ✅ Load Departments (Plant + Employee)
    const loadDepartments = async () => {
        if (!empCode.trim()) return alert("Enter employee code");
        if (!selectedPlant) return alert("Select plant");

        setLoading(true);
        try {
            const res = await MenuProcessService.getDepartmentListData(empCode,selectedPlant);

            const list = res.data.result.map(d => ({
                id: d.id,
                departmentName: d.departmentName,
                assign: d.assign === 1 || d.assign === "1" || d.assign === true
            }));

            setDepartments(list);

            // Reset select all
            setSelectAll(list.length > 0 && list.every(d => d.assign));

        } catch (err) {
            console.error(err);
            alert("Failed to load department rights");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Toggle Single
    const toggleDepartment = (id) => {
        const updated = departments.map(d =>
            d.id === id ? { ...d, assign: !d.assign } : d
        );

        setDepartments(updated);

        // Update Select All state
        const allSelected = updated.every(d => d.assign);
        setSelectAll(allSelected);
    };

    // ✅ Select All / Unselect All
    const handleSelectAll = () => {
        const newValue = !selectAll;

        const updated = departments.map(d => ({
            ...d,
            assign: newValue
        }));

        setDepartments(updated);
        setSelectAll(newValue);
    };

    // ✅ Save Rights
    const saveRights = async () => {
        if (!empCode.trim()) return alert("Enter employee code");

        setSaving(true);
        try {
            const assigned = departments.filter(d => d.assign).map(d => parseInt(d.id));
            const unassigned = departments.filter(d => !d.assign).map(d => parseInt(d.id));

            if (assigned.length > 0) {
                await MenuProcessService.setDepartmentRights({
                    employeeCode: empCode,
                    DepartmentIDs: assigned,
                    assign: true
                });
            }

            if (unassigned.length > 0) {
                await MenuProcessService.setDepartmentRights({
                    employeeCode: empCode,
                    DepartmentIDs: unassigned,
                    assign: false
                });
            }

            alert("Department rights updated!");
        } catch (err) {
            console.error(err);
            alert("Failed to save department rights");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <h4 className="text-left text-primary mb-3">Department Rights</h4>

            <div className="row mb-4 align-items-end">

                <div className="row mb-4 align-items-end">

                    {/* ✅ Plant Dropdown */}
                    <div className="col-md-4">
                        <label className="form-label text-primary">Select Plant</label>
                        <select
                            className="form-select"
                            value={selectedPlant}
                            onChange={handlePlantChange}
                        >
                            <option value="">Select Plant</option>
                            {plants.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.plantName || p.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* ✅ Employee Code */}
                    <div className="col-md-4">
                        <label className="form-label text-primary">Employee Code</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter HR Employee Code"
                            value={empCode}
                            onChange={e => setEmpCode(e.target.value)}
                        />
                    </div>

                    {/* ✅ Search Button */}
                    <div className="col-md-4">
                        <button
                            className="btn btn-primary w-100"
                            onClick={loadDepartments}
                            disabled={loading}
                        >
                            {loading ? "Loading…" : "Search"}
                        </button>
                    </div>

                </div>
            </div>

            {/* ✅ Table */}
            {departments.length > 0 && (
                <>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Department Name</th>
                                <th>
                                    Select All{" "}
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {departments.map(d => (
                                <tr key={d.id}>
                                    <td>{d.id}</td>
                                    <td>{d.departmentName}</td>
                                    <td>
                                        <Switch
                                            checked={d.assign}
                                            onChange={() => toggleDepartment(d.id)}
                                            onColor="#22c55e"
                                            offColor="#ccc"
                                            height={20}
                                            width={48}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* ✅ Save Button */}
                    <button
                        className="btn btn-success mt-3"
                        onClick={saveRights}
                        disabled={saving}
                    >
                        {saving ? "Saving…" : "Save Rights"}
                    </button>
                </>
            )}
        </div>
    );
}

export default DepartmentRights;